import { classnames } from '@aibees/shared/utils';

import { Icon } from '@aibees/shared/components';
import { useAdvanceTableContext } from '@aibees/shared/features/advance-table/context';
import { Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { memo, useCallback, useMemo } from 'react';
import { useGetBrowseIdsApiData } from '../../../table';
import * as Styled from './styles';
import * as SX from './sx';
import type { AdvancedTableSelectPopupWrapperProps as Props } from './types';

export const AdvancedTableSelectPopupWrapper = memo(
  ({
    className,
    sx,
    disabled,
    count,
    children,
    checkboxDisabled,
    state,
    open,
    onClick,
  }: Props) => {
    const { table } = useAdvanceTableContext();
    const browseIdsApi = useGetBrowseIdsApiData(table);
    // Memoize the click handler to prevent recreation on every render
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (disabled || !onClick) return;
        onClick(e);
      },
      [disabled, onClick]
    );

    // Memoize the className to prevent recalculation on every render
    const wrapperClassName = useMemo(
      () => classnames('AdvancedTableSelectPopupWrapper', className),
      [className]
    );

    // Memoize the title text to prevent recalculation on every render
    const titleText = useMemo(
      () =>
        (browseIdsApi?.isFetching ? 'Selecting...' : `${count} Selected`) || '',
      [count, browseIdsApi?.isFetching]
    );

    // Memoize the checkbox props to prevent object recreation
    const checkboxProps = useMemo(
      () => ({
        size: 'small' as const,
        indeterminate: state?.indeterminate,
        checked: false,
        disabled: checkboxDisabled,
        sx: SX.checkbox,
      }),
      [state?.indeterminate, checkboxDisabled]
    );

    // Memoize the wrapper props to prevent object recreation
    const wrapperProps = useMemo(
      () => ({
        className: wrapperClassName,
        sx: SX.root,
        onClick: handleClick,
      }),
      [wrapperClassName, handleClick]
    );

    // Memoize the color to prevent recalculation on every render
    const color = useMemo(() => (count ? 'primary.500' : 'grey.700'), [count]);

    return (
      <Styled.Wrapper {...wrapperProps}>
        <Checkbox {...checkboxProps} />
        <Typography variant="body3" fontWeight={600} color={color}>
          {titleText}
        </Typography>
        <Icon
          name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
          size={16}
          color={color}
        />
      </Styled.Wrapper>
    );
  }
);

AdvancedTableSelectPopupWrapper.displayName = 'AdvancedTableSelectPopupWrapper';
